import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { BadgeDollarSign, Calendar as CalendarIcon, Check, Code, FilePlus, ListPlus, Plus, UserPlus, Users, X } from "lucide-react";
import { TaskAssignee, MFOItem, MFOSubTask } from "@/types/kanban";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { createMFO, updateMFO } from "@/utils/supabase/mfoUtils";
import { useNavigate } from "react-router-dom";
import { useGetEmployees } from "@/hooks/use-employees";
import { supabase } from '@/integrations/supabase/client';

interface CreateMFOModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  mode?: "create" | "edit";
  initialMFO?: MFOItem;
  departments?: {id: string, name: string}[];
  mfoCodes?: {id: string, code: string, title: string}[];
  employees?: {id: string, name: string, avatar?: string}[];
  onMFOCreated?: (mfo: MFOItem) => Promise<void>;
  onSave?: (mfo: MFOItem) => Promise<void>;
}

function toSlug(str) {
  return str?.toLowerCase().replace(/\s+/g, '-').trim();
}

const CreateMFOModal = ({ 
  isOpen, 
  onOpenChange, 
  onSuccess,
  mode = "create",
  initialMFO,
  departments = [],
  mfoCodes = [],
  employees = [],
  onMFOCreated,
  onSave
}: CreateMFOModalProps) => {
  // Form state
  const [title, setTitle] = useState("");
  const [selectedMFOCode, setSelectedMFOCode] = useState("");
  const [indicators, setIndicators] = useState<string[]>([""]);
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState({
    q1: false,
    q2: false,
    q3: false,
    q4: false,
  });
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [departmentId, setDepartmentId] = useState("");
  const [priority, setPriority] = useState<"high" | "medium" | "low">("medium");
  const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>([]);
  const [subtasks, setSubtasks] = useState<{text: string, completed: boolean, assigneeId?: string}[]>([{ text: "", completed: false, assigneeId: "" }]);
  const [status, setStatus] = useState('not-started');
  const [kanbanColumns, setKanbanColumns] = useState<any[]>([]);
  
  const { toast } = useToast();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const navigate = useNavigate();
  const { data: fetchedEmployees = [] } = useGetEmployees();

  // Use provided employees or fetched employees
  const availableEmployees = employees.length > 0 ? employees : fetchedEmployees;

  // Initialize form with existing MFO data if in edit mode
  useEffect(() => {
    if (mode === "edit" && initialMFO) {
      setTitle(initialMFO.title || "");
      setBudget(initialMFO.budget?.toString() || "");
      setTimeline(initialMFO.timeline || { q1: false, q2: false, q3: false, q4: false });
      setDepartmentId(initialMFO.departmentId || "");
      setPriority(initialMFO.priority as "high" | "medium" | "low" || "medium");
      setStatus(initialMFO.status || 'not-started');
      
      if (initialMFO.dueDate) {
        try {
          // Try to parse the due date string to a Date object
          setDueDate(new Date(initialMFO.dueDate));
        } catch (error) {
          console.error("Error parsing due date:", error);
        }
      }
      
      // Set success indicators
      if (initialMFO.successIndicators && initialMFO.successIndicators.length > 0) {
        setIndicators(initialMFO.successIndicators);
      }
      
      // Set collaborators
      if (initialMFO.collaborators && initialMFO.collaborators.length > 0) {
        setSelectedCollaborators(initialMFO.collaborators.map(c => c.id));
      }
      
      // Set subtasks
      if (initialMFO.subTasks && initialMFO.subTasks.length > 0) {
        setSubtasks(initialMFO.subTasks.map(st => ({
          text: st.text,
          completed: st.completed,
          assigneeId: ""  // We don't have assignee in the current data model
        })));
      }
    }
  }, [mode, initialMFO]);

  useEffect(() => {
    // Fetch kanban columns for statuses
    const fetchKanbanColumns = async () => {
      const { data, error } = await supabase
        .from('kanban_columns')
        .select('id, title, icon_color, icon_name');
      if (!error && data) setKanbanColumns(data);
    };
    fetchKanbanColumns();
  }, []);

  const addSuccessIndicator = () => {
    setIndicators([...indicators, ""]);
  };

  const updateIndicator = (index: number, value: string) => {
    const newIndicators = [...indicators];
    newIndicators[index] = value;
    setIndicators(newIndicators);
  };

  const removeIndicator = (index: number) => {
    if (indicators.length > 1) {
      const newIndicators = [...indicators];
      newIndicators.splice(index, 1);
      setIndicators(newIndicators);
    }
  };

  const addSubtask = () => {
    setSubtasks([...subtasks, { text: "", completed: false, assigneeId: "" }]);
  };

  const updateSubtask = (index: number, field: 'text' | 'assigneeId', value: string) => {
    const newSubtasks = [...subtasks];
    newSubtasks[index][field] = value;
    setSubtasks(newSubtasks);
  };

  const removeSubtask = (index: number) => {
    if (subtasks.length > 1) {
      const newSubtasks = [...subtasks];
      newSubtasks.splice(index, 1);
      setSubtasks(newSubtasks);
    }
  };

  const toggleCollaborator = (id: string) => {
    setSelectedCollaborators(prev => 
      prev.includes(id) 
        ? prev.filter(collabId => collabId !== id)
        : [...prev, id]
    );
  };

  const handleReset = () => {
    setTitle("");
    setSelectedMFOCode("");
    setIndicators([""]);
    setBudget("");
    setTimeline({ q1: false, q2: false, q3: false, q4: false });
    setDueDate(undefined);
    setDepartmentId("");
    setPriority("medium");
    setSelectedCollaborators([]);
    setSubtasks([{ text: "", completed: false, assigneeId: "" }]);
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!title || (!selectedMFOCode && mode === "create") || !budget || !departmentId || !dueDate) {
        toast({
          title: "Missing required fields",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      // Filter out empty indicators
      const validIndicators = indicators.filter(ind => ind.trim() !== "");
      if (validIndicators.length === 0) {
        toast({
          title: "Missing success indicators",
          description: "Please add at least one success indicator",
          variant: "destructive",
        });
        return;
      }

      // Check if at least one quarter is selected
      if (!timeline.q1 && !timeline.q2 && !timeline.q3 && !timeline.q4) {
        toast({
          title: "Timeline required",
          description: "Please select at least one quarter for the timeline",
          variant: "destructive",
        });
        return;
      }

      // Filter out empty subtasks
      const validSubtasks = subtasks
        .filter(task => task.text.trim() !== "")
        .map(task => ({
          text: task.text,
          completed: task.completed || false
        }));

      // Convert selected collaborator IDs to full objects
      const collaborators = availableEmployees
        .filter(emp => selectedCollaborators.includes(emp.id))
        .map(emp => ({
          id: emp.id,
          name: emp.name,
          avatar: emp.avatar
        }));

      // Get the selected MFO code
      const selectedCode = mfoCodes.find(code => code.id === selectedMFOCode);
      
      if (mode === "create") {
        // Create new MFO
        const mfoData = {
          code: selectedCode ? selectedCode.code + "A" : "MFO 1A", // Default if no code selected
          title,
          successIndicators: validIndicators,
          budget: parseFloat(budget),
          timeline,
          collaborators,
          departmentId,
          status,
          priority,
          progress: 0,
          dueDate: format(dueDate, "MMM dd, yyyy"),
          subtasks: validSubtasks
        };

        const mfoId = await createMFO(mfoData);
        
        toast({
          title: "Success",
          description: "MFO created successfully",
        });

        // Create a full MFOItem with the returned ID
        const fullMFOItem: MFOItem = {
          ...mfoData,
          id: mfoId,
          departmentName: departments.find(d => d.id === departmentId)?.name || "",
          subTasks: validSubtasks.map(st => ({
            id: "", // IDs will be assigned by the backend
            text: st.text,
            completed: st.completed
          }))
        };

        // Use the specific callback if provided
        if (onMFOCreated) {
          await onMFOCreated(fullMFOItem);
        } else if (onSave) {
          await onSave(fullMFOItem);
        } else if (onSuccess) {
          onSuccess();
        } else {
          navigate(`/dashboard/mfo/${mfoId}`);
        }
      } else if (mode === "edit" && initialMFO) {
        // Update existing MFO
        const updateData = {
          title,
          successIndicators: validIndicators,
          budget: parseFloat(budget),
          timeline,
          collaborators,
          departmentId,
          priority,
          dueDate: format(dueDate, "MMM dd, yyyy"),
          status,
          subtasks: validSubtasks
        };

        await updateMFO(initialMFO.id, updateData);
        
        toast({
          title: "Success",
          description: "MFO updated successfully",
        });

        // Create a full updated MFOItem for callbacks
        const updatedMFOItem: MFOItem = {
          ...initialMFO,
          ...updateData,
          departmentName: departments.find(d => d.id === departmentId)?.name || initialMFO.departmentName,
          subTasks: validSubtasks.map(st => ({
            id: "", // We'll let the backend assign IDs
            text: st.text,
            completed: st.completed
          }))
        };

        // Call success callback or navigate
        if (onMFOCreated) {
          await onMFOCreated(updatedMFOItem);
        } else if (onSave) {
          await onSave(updatedMFOItem);
        } else if (onSuccess) {
          onSuccess();
        }
      }

      handleReset();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving MFO:", error);
      toast({
        title: "Error",
        description: "Failed to save MFO. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePlus className="h-5 w-5 text-primary" />
            {mode === "create" ? "Create New MFO" : "Edit MFO"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create" 
              ? "Create a new Major Final Output (MFO) with details and subtasks" 
              : "Edit this Major Final Output (MFO) and update its details"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* MFO Code dropdown - only show in create mode */}
            {mode === "create" && (
              <div className="grid gap-2">
                <Label htmlFor="mfo-code" className="flex items-center gap-1">
                  <Code className="h-4 w-4" /> MFO Code *
                </Label>
                <select
                  id="mfo-code"
                  value={selectedMFOCode}
                  onChange={(e) => setSelectedMFOCode(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                >
                  <option value="">Select an MFO Code</option>
                  {mfoCodes.map((code) => (
                    <option key={code.id} value={code.id}>
                      {code.code} - {code.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Department dropdown */}
            <div className="grid gap-2">
              <Label htmlFor="department" className="flex items-center gap-1">
                Department *
              </Label>
              <select
                id="department"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              >
                <option value="">Select a Department</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* MFO Title */}
          <div className="grid gap-2">
            <Label htmlFor="mfo-title">MFO/PAP Title *</Label>
            <Input
              id="mfo-title"
              placeholder="Enter MFO title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Success Indicators */}
          <div className="grid gap-2">
            <Label className="flex items-center justify-between">
              <span>Success Indicators *</span>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addSuccessIndicator}
                className="h-7 px-2"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Indicator
              </Button>
            </Label>
            <div className="space-y-2">
              {indicators.map((indicator, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder={`Indicator ${index + 1}`}
                    value={indicator}
                    onChange={(e) => updateIndicator(index, e.target.value)}
                  />
                  {indicators.length > 1 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeIndicator(index)}
                      className="h-10 px-2.5"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Budget */}
            <div className="grid gap-2">
              <Label htmlFor="mfo-budget" className="flex items-center gap-1">
                <BadgeDollarSign className="h-4 w-4" /> Allocated Budget *
              </Label>
              <Input
                id="mfo-budget"
                placeholder="Enter budget amount"
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
              />
            </div>

            {/* Priority */}
            <div className="grid gap-2">
              <Label htmlFor="priority">Priority</Label>
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as "high" | "medium" | "low")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Timeline Quarters */}
            <div className="grid gap-2">
              <Label>Timeline (Quarters) *</Label>
              <div className="flex flex-col gap-2 p-3 border rounded-md">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="q1" 
                    checked={timeline.q1} 
                    onCheckedChange={(checked) => 
                      setTimeline({...timeline, q1: checked === true})
                    }
                  />
                  <label htmlFor="q1" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                    Quarter 1 (Jan-Mar)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="q2" 
                    checked={timeline.q2} 
                    onCheckedChange={(checked) => 
                      setTimeline({...timeline, q2: checked === true})
                    }
                  />
                  <label htmlFor="q2" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                    Quarter 2 (Apr-Jun)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="q3" 
                    checked={timeline.q3} 
                    onCheckedChange={(checked) => 
                      setTimeline({...timeline, q3: checked === true})
                    }
                  />
                  <label htmlFor="q3" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                    Quarter 3 (Jul-Sep)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="q4" 
                    checked={timeline.q4} 
                    onCheckedChange={(checked) => 
                      setTimeline({...timeline, q4: checked === true})
                    }
                  />
                  <label htmlFor="q4" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                    Quarter 4 (Oct-Dec)
                  </label>
                </div>
              </div>
            </div>

            {/* Due Date */}
            <div className="grid gap-2">
              <Label>Due Date *</Label>
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "PPP") : <span>Pick a deadline date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-50 bg-background" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={(date) => {
                      setDueDate(date);
                      setIsCalendarOpen(false);
                    }}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Collaborators */}
          <div className="grid gap-2">
            <Label className="flex items-center gap-1">
              <Users className="h-4 w-4" /> Collaborators
            </Label>
            <div className="border rounded-md p-2 max-h-[150px] overflow-y-auto">
              {availableEmployees.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">No employees available</p>
              ) : (
                availableEmployees.map((employee) => (
                  <div 
                    key={employee.id} 
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded-md"
                  >
                    <input 
                      type="checkbox"
                      id={`collab-${employee.id}`}
                      checked={selectedCollaborators.includes(employee.id)}
                      onChange={() => toggleCollaborator(employee.id)}
                      className="h-4 w-4"
                    />
                    <label 
                      htmlFor={`collab-${employee.id}`}
                      className="flex-grow cursor-pointer"
                    >
                      {employee.name}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Subtasks */}
          <div className="grid gap-2">
            <Label className="flex items-center justify-between">
              <span className="flex items-center gap-1">
                <ListPlus className="h-4 w-4" /> Subtasks
              </span>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addSubtask}
                className="h-7 px-2"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Subtask
              </Button>
            </Label>
            <div className="space-y-2">
              {subtasks.map((subtask, index) => (
                <div key={index} className="grid gap-2 border rounded-md p-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Subtask description"
                      value={subtask.text}
                      onChange={(e) => updateSubtask(index, 'text', e.target.value)}
                    />
                    {subtasks.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeSubtask(index)}
                        className="h-10 px-2.5"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div>
                    <Label htmlFor={`assignee-${index}`} className="flex items-center gap-1 mb-1">
                      <UserPlus className="h-3.5 w-3.5" /> Assign to
                    </Label>
                    <select
                      id={`assignee-${index}`}
                      value={subtask.assigneeId || ""}
                      onChange={(e) => updateSubtask(index, 'assigneeId', e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select an assignee</option>
                      {availableEmployees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status dropdown */}
          <div className="grid gap-2">
            <Label htmlFor="status">Status *</Label>
            <select
              id="status"
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            >
              {kanbanColumns.map(col => (
                <option key={col.id} value={toSlug(col.title)}>
                  {col.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleSave();
            }}
            disabled={false}
          >
            <Check className="h-4 w-4 mr-2" />
            {mode === "create" ? "Create MFO" : "Update MFO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateMFOModal;
